import { ArrowRight, Check } from "lucide-react";
import { Reveal, SectionHead } from "./shared";

const TIERS = [
    {
        name: "Starter",
        tagline: "For businesses ready to stop missing leads.",
        features: ["AI lead response", "Missed call text back", "Basic follow-up", "Appointment booking", "SMS automation"],
        testid: "pricing-tier-starter",
    },
    {
        name: "Growth",
        tagline: "For businesses that want a complete automated lead system.",
        badge: "Most Popular",
        highlight: true,
        features: [
            "Everything in Starter",
            "Advanced follow-up",
            "Email automation",
            "Lead nurturing",
            "Review automation",
            "CRM automation",
            "Reactivation campaigns",
        ],
        testid: "pricing-tier-growth",
    },
    {
        name: "Pro",
        tagline: "For businesses wanting a fully managed automation system.",
        features: [
            "Everything in Growth",
            "Custom AI workflows",
            "Advanced integrations",
            "Custom automations",
            "Priority support",
            "Strategy and optimization",
        ],
        testid: "pricing-tier-pro",
    },
];

export default function Pricing() {
    return (
        <section id="pricing" className="py-24 sm:py-32 relative overflow-hidden" data-testid="pricing-section">
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-cyan-500/[0.06] blur-[140px] rounded-full pointer-events-none" />
            <div className="container-x relative">
                <SectionHead
                    eyebrow="10 — Investment"
                    title="Let's Build the Right System for Your Business."
                    sub="Plans start at $299/month with no setup fee. Every system is tailored — your free automation audit confirms exactly which setup fits your lead flow."
                    testid="pricing-heading"
                />
                <div className="mt-16 grid md:grid-cols-3 gap-5 items-stretch">
                    {TIERS.map((t, i) => (
                        <Reveal key={t.name} delay={i * 0.1} className="h-full">
                            <div
                                className={`rounded-3xl p-8 h-full flex flex-col relative overflow-hidden transition-[transform,border-color] duration-300 hover:-translate-y-1 ${
                                    t.highlight
                                        ? "border border-cyan-400/40 bg-cyan-400/[0.06] shadow-[0_0_60px_-20px_rgba(0,240,255,0.3)]"
                                        : "glass"
                                }`}
                                data-testid={t.testid}
                            >
                                {t.badge && (
                                    <span className="absolute top-5 right-5 ff-mono text-[9px] uppercase tracking-[0.15em] text-cyan-200 bg-cyan-400/15 border border-cyan-400/40 rounded-full px-3 py-1">
                                        {t.badge}
                                    </span>
                                )}
                                <h3 className="ff-display text-2xl font-extrabold text-white tracking-tight">{t.name}</h3>
                                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{t.tagline}</p>
                                <ul className="mt-8 space-y-3.5 flex-1">
                                    {t.features.map((f) => (
                                        <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                                            <Check className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <a
                                    href="#book"
                                    className={`${t.highlight ? "btn-primary" : "btn-ghost"} mt-10 w-full`}
                                    data-testid={`${t.testid}-cta`}
                                >
                                    Book a Free Automation Audit
                                </a>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.2}>
                    <p className="mt-10 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                        <ArrowRight className="w-4 h-4 text-cyan-400" />
                        All systems include white-glove setup — we build it, you watch it work.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}
